/**
 * useMarker - Singleton hook for loading Marker.io bug reporting widget
 *
 * Gated: the widget only loads when `enabled` is true (e.g. admin users),
 * so non-admin visitors never load marker.io or see the reporting tool.
 */

import { useState, useEffect } from 'react';
import markerSDK, { type MarkerSdk } from '@marker.io/browser';

let widgetPromise: Promise<MarkerSdk> | null = null;

export function useMarker(enabled: boolean): { widget: MarkerSdk | null; isLoaded: boolean; error: string | null } {
  const [widget, setWidget] = useState<MarkerSdk | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let isMounted = true;

    if (!widgetPromise) {
      widgetPromise = markerSDK.loadWidget({
        project: '6998d26b9f610ff8b81c2309',
      });
    }

    widgetPromise
      .then((w) => {
        if (!isMounted) return;
        setWidget(w);
        setIsLoaded(true);
      })
      .catch((err) => {
        // Clear the singleton so a transient failure can be retried on the next mount.
        widgetPromise = null;
        if (!isMounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load Marker.io');
      });

    return () => {
      isMounted = false;
    };
  }, [enabled]);

  return { widget, isLoaded, error };
}
